import { AtomField, AtomizedForm } from 'atoms/form-atoms';
import { InformationFilled } from '@carbon/react/icons';
import {
  Checkbox,
  Column,
  FlexGrid as Grid,
  Row,
  DefinitionTooltip,
} from '@carbon/react';
import { NumberField } from 'components/form-fields/number-field';
import { ScenariosForm } from '../types';
import { useAtomValue, useSetAtom } from 'jotai';
import style from './infill-assumptions.module.scss';

const InfillAssumptions = ({
  form,
}: {
  form:
    | AtomizedForm<
        ScenariosForm['globalAssumptions']['drillingAssumptionAttributes']['infillAttributes']
      >
    | AtomizedForm<
        ScenariosForm['tiersAttributes'][0]['drillingAssumptionAttributes']['infillAttributes']
      >;
}) => {
  const IncludeUpsideCheckbox = ({
    id,
    field,
    label,
  }: {
    id: string;
    field: AtomField<boolean> | AtomField<boolean | null>;
    label: string;
  }) => {
    const setValue = useSetAtom(field);
    const fieldState = useAtomValue(field);
    return (
      <div className={style.tooltipRow}>
        <Checkbox
          id={id}
          checked={fieldState.value === null ? false : fieldState.value}
          onChange={(_evt, { checked }: { checked: boolean }) =>
            setValue(checked)
          }
          labelText={label}
        />
        <DefinitionTooltip
          align="top"
          definition="If checked, active upside LTDs will be included in evaluations and the limit checks below"
          openOnHover
        >
          <InformationFilled />
        </DefinitionTooltip>
      </div>
    );
  };

  return (
    <div className={style.infillAssumptions}>
      <h3>Infill Assumptions</h3>
      <Grid fullWidth className="bx--no-gutter">
        <Row>
          <Column className={style.flexContainer}>
            <IncludeUpsideCheckbox
              id="infillIncludeUpside"
              field={form.includeUpside}
              label="Include Active Upside LTDs"
            />
          </Column>
        </Row>
        <Row className={style.rowThirds}>
          <Column className={style.relativeColumns}>
            <DefinitionTooltip
              className={style.toolTip}
              align="top"
              definition="If >=X% of the slots within a single bench are filled (PDP, DUC, PMT, Shut-In), kill LTDs in that bench"
              openOnHover
            >
              <InformationFilled />
            </DefinitionTooltip>
            <NumberField
              id="infillFormationLimit"
              label="Formation Limit (%)"
              min={0}
              invalidText="Must be positive number"
              field={form.formationLimitPercentage}
            />
          </Column>
          <Column className={style.relativeColumns}>
            <DefinitionTooltip
              className={style.toolTip}
              align="top"
              definition="If >=X% of the slots in the unit are filled, kill LTDs in the unit"
              openOnHover
            >
              <InformationFilled />
            </DefinitionTooltip>
            <NumberField
              id="infillDevelopmentAreaLimit"
              label="Development Area Limit (%)"
              min={0}
              invalidText="Must be positive number"
              field={form.developmentAreaLimitPercentage}
            />
          </Column>
          <Column className={style.relativeColumns}>
            <DefinitionTooltip
              className={style.toolTip}
              align="top"
              definition="If there are fewer than X LTDs in the unit, kill them"
              openOnHover
            >
              <InformationFilled />
            </DefinitionTooltip>
            <NumberField
              id="infillLtdLimit"
              label="LTD Minimum"
              min={0}
              invalidText="Must be positive number"
              field={form.ltdLimit}
            />
          </Column>
        </Row>
      </Grid>
    </div>
  );
};

export { InfillAssumptions };
